(() => {
  if (customElements.get('maxton-set-builder')) return;

  // Constants
  const PRODUCTS_PER_CATEGORY = 50;
  const BUNDLE_TAG = 'SET';
  const HIDDEN_PRODUCT_TAG = 'hide-on-pdp';
  const MULTI_SELECT_TAGS = ['INNE', 'CAP', 'SPOILER'];
  const TILE_IMAGE_WIDTH = 360;
  const COLLAPSED_TILE_LIMIT = 4;
  const RELATED_PANEL_HIDDEN_TAGS = ['RSD'];
  const DRIVER_LINKED_ONLY_TAGS = ['RSD'];
  const START_PRODUCT_PARAM = 'product';
  const VARIANT_FILTER_ALL = 'all';
  const VARIANT_GROUPS = [
    { id: 'top', tag: 'CAP-TOP', textKey: 'variantTop', icon: 'M4 7h16v3H4z' },
    { id: 'bottom', tag: 'CAP-BOTTOM', textKey: 'variantBottom', icon: 'M4 14h16v3H4z' },
  ];
  const VERSION_PATTERN = /\bV\.\d+\b/i;
  const FIT_SEPARATORS = [' pasujący do ', ' pasujące do ', ' pasująca do ', ' suitable for ',' compatible with ', ' passend für ', ' compatible avec '];
  const LOADER_MIN_DURATION_MS = 1000;

  // Generic helpers
  const createElement = (tagName, { className, text, attributes = {} } = {}, children = []) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    Object.entries(attributes).forEach(([name, value]) => {
      if (value !== undefined && value !== null && value !== false) element.setAttribute(name, value === true ? '' : value);
    });
    element.append(...children.filter(Boolean));
    return element;
  };

  const readNumericId = (gid = '') => String(gid).split('/').pop();

  const readVersionToken = (title = '') => title.match(VERSION_PATTERN)?.[0]?.toUpperCase() ?? null;

  const readVersionNumber = ({ version }) => (version ? Number(version.slice(2)) : Number.POSITIVE_INFINITY);

  const compareByVersionThenTitle = (left, right) =>
    readVersionNumber(left) - readVersionNumber(right) || left.shortTitle.localeCompare(right.shortTitle);

  const buildShortTitle = (title = '') => {
    const separator = FIT_SEPARATORS.find((candidate) => title.includes(candidate));
    return separator ? title.split(separator)[0].trim() : title;
  };

  const buildProductUrl = (handle) => `${window.Shopify?.routes?.root ?? '/'}products/${handle}`;

  const buildResizedImageUrl = (src, width) => {
    if (!src) return null;
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', width);
    return url.toString();
  };
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Proxy catalog
  const fetchVehicleCatalog = async ({ apiBase, collectionId, country, language }) => {
    const params = new URLSearchParams({ limit: PRODUCTS_PER_CATEGORY, country, language });
    const response = await fetch(`${apiBase}/api/collection/${encodeURIComponent(collectionId)}?${params}`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const { data } = await response.json();
    return data;
  };

  const normalizeCatalogProduct = (product, categoryTags) => ({
    id: readNumericId(product.id),
    variantId: readNumericId(product.selectedOrFirstAvailableVariant?.id),
    handle: product.handle,
    title: product.title,
    shortTitle: buildShortTitle(product.title),
    version: readVersionToken(product.title),
    material: product.metafield?.value ?? '',
    price: Number.parseFloat(product.priceRange?.minVariantPrice?.amount ?? 'NaN'),
    currency: product.priceRange?.minVariantPrice?.currencyCode,
    imageUrl: buildResizedImageUrl(product.featuredImage?.url, TILE_IMAGE_WIDTH),
    imageAlt: product.featuredImage?.altText ?? product.title,
    categoryTags,
    variantGroupId: VARIANT_GROUPS.find(({ tag }) => (product.tags ?? []).some((productTag) => String(productTag).toUpperCase() === tag))?.id ?? null,
  });

  const BUNDLE_COMPONENTS_PATTERN = /data-components="([\d,\s]+)"/;

  const parseIdList = (value = '') => value.split(',').map((id) => id.trim()).filter(Boolean);

  const fetchBundleComponentIdsFromPage = async (handle) => {
    const response = await fetch(buildProductUrl(handle));
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return parseIdList((await response.text()).match(BUNDLE_COMPONENTS_PATTERN)?.[1]);
  };

  const normalizeTagList = (tags = []) => tags.map((tag) => String(tag).trim().toUpperCase()).filter(Boolean);

  const normalizeCompatibilityConfig = ({ map = {}, driverTags = [], restrictedTags = [], label = '', exclusiveLabel = '', linkedLabel = '' } = {}) => ({
    map,
    driverTags: normalizeTagList(driverTags),
    restrictedTags: normalizeTagList(restrictedTags),
    label,
    exclusiveLabel: exclusiveLabel || label,
    linkedLabel: linkedLabel || 'Pasuje do wybranego produktu',
  });

  const isHiddenOnProductPage = ({ tags = [] }) => tags.some((tag) => tag.toLowerCase() === HIDDEN_PRODUCT_TAG);

  const collectCatalogProducts = (categories = []) =>
    categories.flatMap(({ handles = [], products = [] }) => {
      const categoryTags = handles.map((tag) => tag.toUpperCase());
      return products.filter((product) => !isHiddenOnProductPage(product)).map((product) => normalizeCatalogProduct(product, categoryTags));
    }).sort(compareByVersionThenTitle);

  // Set builder element
  class MaxtonSetBuilder extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;

      this.config = JSON.parse(this.querySelector('[data-set-builder-config]').textContent);
      this.config.compatibility = normalizeCompatibilityConfig(this.config.compatibility);
      this.elements = {
        title: this.querySelector('[data-set-builder-title]'),
        steps: this.querySelector('[data-set-builder-steps]'),
        summaryTitle: this.querySelector('[data-set-builder-summary-title]'),
        summaryList: this.querySelector('[data-set-builder-summary-list]'),
        total: this.querySelector('[data-set-builder-total]'),
        addToCart: this.querySelector('[data-set-builder-add]'),
        status: this.querySelector('[data-set-builder-status]'),
        startBanner: this.querySelector('[data-set-builder-start]'),
      };
      this.state = { materialGroupId: null, selections: new Map(), products: [], expandedStepIds: new Set(),variantFilters: new Map() };
      this.bundleComponentCache = new Map(Object.entries(this.config.bundleComponentMap ?? {}).filter(([, ids]) => ids.length));
      this.moneyFormatter = null;

      this.elements.addToCart.addEventListener('click', () => this.addSelectionToCart());
      this.loadCatalog();
    }

    // Data loading
    async loadCatalog() {
      const loaderStartedAt = performance.now();
      try {
        const catalog = await fetchVehicleCatalog(this.config);
        this.state.products = collectCatalogProducts(catalog.categories);
        this.vehicleLabel = catalog.fits?.value || this.config.vehicleLabel;
        this.applyInitialSelection();
        this.render();
        this.renderStartBanner();
        await this.waitForLoaderMinimum(loaderStartedAt);
        this.hidden = false;
      } catch (error) {
        console.warn('[set-builder] catalog unavailable', error);
        this.hidden = true;
      } finally {
        this.hideLoader();
      }
    }

    // Loader (standalone page): stays visible for at least LOADER_MIN_DURATION_MS
    get loaderElement() {
      return this.parentElement?.querySelector('[data-set-builder-loader]') ?? null;
    }

    waitForLoaderMinimum(startedAt) {
      if (!this.loaderElement) return Promise.resolve();
      return wait(Math.max(LOADER_MIN_DURATION_MS - (performance.now() - startedAt), 0));
    }

    hideLoader() {
      this.loaderElement?.remove();
    }

    // Materials and steps
    get materialGroups() {
      return this.config.materialGroups.filter((group) => this.getProductsForMaterial(group).length);
    }
          // Single material: show only its tile, without "all collections"
          get specificMaterialGroups() {
            return this.materialGroups.filter(({ materials }) => materials.length);
          }
    
          get hasMaterialChoice() {
            return this.specificMaterialGroups.length > 1;
          }
    
          get displayedMaterialGroups() {
            return this.hasMaterialChoice || !this.specificMaterialGroups.length ? this.materialGroups : this.specificMaterialGroups;
          }
    
          get displayedActiveMaterialGroup() {
            return this.displayedMaterialGroups.find(({ id }) => id === this.activeMaterialGroup.id) ?? this.displayedMaterialGroups[0];
          }

    get activeMaterialGroup() {
      return this.config.materialGroups.find(({ id }) => id === this.state.materialGroupId) ?? this.config.materialGroups[0];
    }

    getProductsForMaterial(group) {
      return group.materials.length
        ? this.state.products.filter(({ material }) => group.materials.includes(material))
        : this.state.products;
    }

    isBundleStep(step) {
      return step.tags.includes(BUNDLE_TAG);
    }

    getBundleComponentIds(bundleId) {
      return (this.bundleComponentCache.get(String(bundleId)) ?? []).map(String);
    }

    async loadBundleComponentIds(bundle) {
      if (!this.bundleComponentCache.has(bundle.id)) {
        const componentIds = await fetchBundleComponentIdsFromPage(bundle.handle).catch((error) => {
          console.warn('[set-builder] bundle components unavailable', bundle.handle, error);
          return [];
        });
        this.bundleComponentCache.set(bundle.id, componentIds);
      }
      return this.getBundleComponentIds(bundle.id);
    }

    getStepCandidates(step, group = this.activeMaterialGroup) {
      return this.getProductsForMaterial(group).filter(({ categoryTags }) => step.tags.some((tag) => categoryTags.includes(tag)));
    }

    get nonBundleSteps() {
      return this.config.steps.filter((step) => !this.isBundleStep(step));
    }

    getStepProducts(step, group = this.activeMaterialGroup) {
      const compatibleProducts = this.getStepCandidates(step, group).filter((product) => this.isCompatibleWithSelection(product));
      return this.isDriverLinkedOnlyStep(step) ? this.narrowToDriverLinkedProducts(compatibleProducts) : compatibleProducts;
    }

    isDriverLinkedOnlyStep(step) {
      return step.tags.some((tag) => DRIVER_LINKED_ONLY_TAGS.includes(tag));
    }

    // Selected rear part narrows RSD steps to its linked products (when it has any there)
    narrowToDriverLinkedProducts(products) {
      if (!this.selectedDriverProducts.length) return products;
      const driverTags = this.config.compatibility?.driverTags ?? [];
      const linkedProducts = products.filter((product) => !this.hasAnyTag(product, driverTags) && this.isUnlockedProduct(product));
      if (!linkedProducts.length) return products;
      return products.filter((product) => this.hasAnyTag(product, driverTags) || linkedProducts.includes(product));
    }

    // Compatibility (caution list)
    getCompatibility(productId) {
      return this.config.compatibility?.map?.[productId] ?? { compatible: [], nonCompatible: [] };
    }

    hasAnyTag({ categoryTags }, tags = []) {
      return tags.some((tag) => categoryTags.includes(tag));
    }

    // Only rules pointing at rear parts (driver tags) restrict a product
    getRuleDriverIds(product) {
      const driverTags = this.config.compatibility?.driverTags ?? [];
      return this.getCompatibility(product.id).nonCompatible.filter((id) => {
        const ruleProduct = this.findProduct(id);
        return ruleProduct && this.hasAnyTag(ruleProduct, driverTags);
      });
    }

    // Any non-rear product whose not-compatible rule points at a rear part needs that rear part
    isRestrictedProduct(product) {
      return !this.hasAnyTag(product, this.config.compatibility?.driverTags) && this.getRuleDriverIds(product).length > 0;
    }

    get selectedDriverProducts() {
      const selectedIds = [...this.state.selections.values()].flat();
      return selectedIds.map((id) => this.findProduct(id)).filter((product) => product && this.hasAnyTag(product, this.config.compatibility?.driverTags));
    }

    isCompatibleWithSelection(product) {
      return !this.isRestrictedProduct(product) || !this.selectedDriverProducts.length || this.isUnlockedProduct(product);
    }

    isUnlockedByDriver(product) {
      return this.selectedDriverProducts.length > 0 && this.isUnlockedProduct(product);
    }

    isLinkedFromDriverSide(product, driver) {
      return this.getCompatibility(driver.id).compatible.includes(product.id);
    }

    isLinkedFromProductSide(product, driver) {
      const { compatible, nonCompatible } = this.getCompatibility(product.id);
      return compatible.includes(driver.id) || nonCompatible.includes(driver.id);
    }

    isExclusiveToSelectedDriver(product) {
      return this.selectedDriverProducts.some(
        (driver) => this.isLinkedFromDriverSide(product, driver) && this.isLinkedFromProductSide(product, driver),
      );
    }

    getTileBadge(product) {
      return product.id === this.startProduct?.id ? this.config.texts.startBadge : this.getCompatibilityBadge(product);
    }

    getCompatibilityBadge(product) {
      const { label, exclusiveLabel, linkedLabel } = this.config.compatibility;
      if (this.isUnlockedByDriver(product)) return this.isExclusiveToSelectedDriver(product) ? exclusiveLabel : label;
      return this.isLinkedToSelectedPart(product) ? linkedLabel : '';
    }

    // Selected non-rear product (e.g. side splitter) that lists this product or is required by it
    isLinkedToSelectedPart(product) {
      const driverTags = this.config.compatibility?.driverTags ?? [];
      return [...this.state.selections.values()]
        .flat()
        .filter((id) => id !== product.id)
        .map((id) => this.findProduct(id))
        .some((part) => part && !this.hasAnyTag(part, driverTags) && this.isUnlockedBy(part, product));
    }

    isUnlockedBy(driver, product) {
      return this.getCompatibility(driver.id).compatible.includes(product.id) || this.getCompatibility(product.id).nonCompatible.includes(driver.id);
    }

    isUnlockedProduct(product) {
      return this.selectedDriverProducts.some((driver) => this.isUnlockedBy(driver, product));
    }

    // Required products (yellow panel): rear parts the product needs plus other parts from its not-compatible list
    getRequiredDrivers(product) {
      if (!this.isRestrictedProduct(product) || this.isUnlockedProduct(product)) return [];
      const driverTags = this.config.compatibility?.driverTags ?? [];
      return this.partSteps.flatMap((step) =>
        this.getStepCandidates(step)
          .filter((driver) => this.hasAnyTag(driver, driverTags) && this.isUnlockedBy(driver, product))
          .map((driver) => ({ product: driver, step })),
      );
    }

    getRequiredParts(product) {
      const driverTags = this.config.compatibility?.driverTags ?? [];
      const requiredIds = new Set(this.getCompatibility(product.id).nonCompatible);
      const requiredParts = this.partSteps.flatMap((step) =>
        this.getStepCandidates(step)
          .filter((part) => requiredIds.has(part.id) && !this.hasAnyTag(part, driverTags))
          .map((part) => ({ product: part, step })),
      );
      const isSatisfied = requiredParts.some(({ product: part, step }) => this.isProductSelected(part, step));
      return isSatisfied ? [] : requiredParts;
    }

    getRequiredProducts(product) {
      return [...this.getRequiredDrivers(product), ...this.getRequiredParts(product)];
    }

    pruneUnavailableSelections() {
      this.nonBundleSteps.forEach((step) => {
        const availableIds = new Set(this.getStepProducts(step).map(({ id }) => id));
        const selectedIds = this.getSelectedIds(step);
        const keptIds = selectedIds.filter((id) => availableIds.has(id));
        if (keptIds.length !== selectedIds.length) this.setSelectedIds(step, keptIds);
      });
    }

    get visibleSteps() {
      return this.config.steps.filter((step) => this.getStepProducts(step).length);
    }

    get partSteps() {
      return this.visibleSteps.filter((step) => !this.isBundleStep(step));
    }

    findProduct(productId) {
      return this.state.products.find(({ id }) => id === productId) ?? null;
    }

    findPartStepForProduct(product) {
      return this.nonBundleSteps.find((step) => this.getStepCandidates(step).some(({ id }) => id === product.id)) ?? null;
    }

    // Related products (green panel): owner's compatible list
    getRelatedProductIds(owner) {
      const relatedIds = new Set(this.getCompatibility(owner.id).compatible);
      relatedIds.delete(owner.id);
      return relatedIds;
    }

    getRelatedProducts(owner) {
      const ownerStep = this.findPartStepForProduct(owner);
      const relatedIds = this.getRelatedProductIds(owner);
      const isRearOwner = this.hasAnyTag(owner, this.config.compatibility?.driverTags);
      const isHiddenFromPanel = (product) => isRearOwner && this.hasAnyTag(product, RELATED_PANEL_HIDDEN_TAGS);

      return this.nonBundleSteps
        .filter((step) => step !== ownerStep)
        .flatMap((step) =>
          this.getStepCandidates(step)
            .filter((product) => relatedIds.has(product.id) && !isHiddenFromPanel(product))
            .map((product) => ({ product, step })),
        );
    }

    isProductSelected(product, step) {
      return this.getSelectedIds(step).includes(product.id);
    }

    toggleRelatedProduct(owner, { product, step }) {
      const ownerStep = this.findPartStepForProduct(owner);
      if (this.isProductSelected(product, step)) {
        this.setSelectedIds(step, this.getSelectedIds(step).filter((id) => id !== product.id));
        this.render();
        return;
      }
      if (ownerStep && !this.isProductSelected(owner, ownerStep)) {
        this.setSelectedIds(ownerStep, this.isMultiSelectStep(ownerStep) ? [...this.getSelectedIds(ownerStep), owner.id] : [owner.id]);
      }
      this.toggleStepProduct(step, product.id);
    }

    // Selection
    isMultiSelectStep(step) {
      return step.tags.some((tag) => MULTI_SELECT_TAGS.includes(tag));
    }

    getSelectedIds(step) {
      return this.state.selections.get(step.id) ?? [];
    }

    setSelectedIds(step, productIds) {
      this.state.selections.set(step.id, productIds.filter(Boolean));
    }

    findMaterialGroupForProduct(product) {
      return this.config.materialGroups.find(({ materials }) => materials.includes(product?.material))?.id ?? this.config.materialGroups[0].id;
    }

    findStepComponents(step, components) {
      const stepComponents = components.filter(({ categoryTags }) => step.tags.some((tag) => categoryTags.includes(tag)));
      return this.isMultiSelectStep(step) ? stepComponents : stepComponents.slice(0, 1);
    }

    // Rear parts that would block an already chosen restricted product are never auto-picked
    conflictsWithComponents(candidate, components) {
      if (!this.hasAnyTag(candidate, this.config.compatibility?.driverTags)) return false;
      return components.some((component) => this.isRestrictedProduct(component) && !this.isUnlockedBy(candidate, component));
    }

    pickDefaultStepProduct(step, preferredVersion, components = []) {
      const stepProducts = this.getStepProducts(step).filter((candidate) => !this.conflictsWithComponents(candidate, components));
      return stepProducts.find(({ version }) => preferredVersion && version === preferredVersion) ?? (step.allowSkip ? null : stepProducts[0] ?? null);
    }

    applyComponentSelection(componentIds, { isBundle, preferredVersion = null }) {
      const components = this.state.products.filter(({ id }) => componentIds.includes(id));
      this.state.materialGroupId = components.length ? this.findMaterialGroupForProduct(components[0]) : this.config.materialGroups[0].id;

      this.nonBundleSteps.forEach((step) => {
        const stepComponents = this.findStepComponents(step, components);
        const products = stepComponents.length || isBundle ? stepComponents : [this.pickDefaultStepProduct(step, preferredVersion, components)];
        this.setSelectedIds(step, products.map((product) => product?.id));
      });
    }

    // Starting product: current PDP product or ?product= on the standalone page
    get startProductId() {
      const { currentProductId, isStandalone } = this.config;
      const urlProductId = isStandalone ? new URLSearchParams(window.location.search).get(START_PRODUCT_PARAM) : null;
      const productId = currentProductId ?? urlProductId;
      return productId ? String(productId) : null;
    }

    get startProduct() {
      return this.config.isStandalone && this.startProductId ? this.findProduct(this.startProductId) : null;
    }

    applyInitialSelection() {
      const { startProductId } = this;
      const pageComponentIds = parseIdList(document.querySelector('[data-bundle-preview][data-components]')?.dataset.components);
      if (startProductId && !this.bundleComponentCache.has(startProductId) && pageComponentIds.length) {
        this.bundleComponentCache.set(startProductId, pageComponentIds);
      }
      const bundleComponentIds = startProductId ? this.getBundleComponentIds(startProductId) : [];
      const isBundle = bundleComponentIds.length > 0;
      const startProductTitle = this.config.currentProductTitle ?? this.findProduct(startProductId)?.title ?? '';

      this.applyComponentSelection(isBundle ? bundleComponentIds : [startProductId].filter(Boolean), {
        isBundle,
        preferredVersion: readVersionToken(startProductTitle),
      });
    }

    isBundleSelected(bundleId) {
      const selectedIds = new Set(this.selectedProducts.map(({ id }) => id));
      const componentIds = this.getBundleComponentIds(bundleId).filter((id) => this.findProduct(id));
      return componentIds.length > 0 && componentIds.every((id) => selectedIds.has(id)) && componentIds.length === selectedIds.size;
    }

    selectMaterialGroup(groupId) {
      this.state.materialGroupId = groupId;

      this.partSteps.forEach((step) => {
        const availableIds = new Set(this.getStepProducts(step).map(({ id }) => id));
        const currentProducts = this.getSelectedIds(step).map((id) => this.findProduct(id)).filter(Boolean);
        const keptIds = currentProducts.filter(({ id }) => availableIds.has(id)).map(({ id }) => id);
        const lostVersions = currentProducts.filter(({ id }) => !availableIds.has(id)).map(({ version }) => version);
        const needsReplacement = !keptIds.length && (lostVersions.length || !step.allowSkip);
        const replacement = needsReplacement ? this.pickDefaultStepProduct(step, lostVersions[0]) : null;
        this.setSelectedIds(step, [...keptIds, replacement?.id]);
      });

      this.render();
    }

    toggleStepProduct(step, productId) {
      const selectedIds = this.getSelectedIds(step);
      if (!this.isMultiSelectStep(step)) {
        this.setSelectedIds(step, [productId]);
      } else if (selectedIds.includes(productId)) {
        this.setSelectedIds(step, selectedIds.filter((id) => id !== productId));
      } else {
        this.setSelectedIds(step, [...selectedIds, productId]);
      }
      this.render();
    }

    clearStepSelection(step) {
      this.setSelectedIds(step, []);
      this.render();
    }
    // TODO: remove if boss do not need X there
    removeSelectedProduct(productId) {
      this.partSteps.forEach((step) => {
        const selectedIds = this.getSelectedIds(step);
        if (selectedIds.includes(productId)) this.setSelectedIds(step, selectedIds.filter((id) => id !== productId));
      });
      this.render();
    }

    async selectBundle(bundle) {
      const componentIds = await this.loadBundleComponentIds(bundle);
      if (!componentIds.length) return;
      this.applyComponentSelection(componentIds, { isBundle: true });
      this.render();
    }

    get selectedProducts() {
      return this.partSteps.flatMap((step) => this.getSelectedIds(step).map((id) => this.findProduct(id))).filter(Boolean);
    }

    // Money
    formatMoney(amount, currency) {
      if (!Number.isFinite(amount)) return '';
      this.moneyFormatter ??= new Intl.NumberFormat(this.config.locale, { style: 'currency', currency });
      return this.moneyFormatter.format(amount);
    }

    // Rendering
    render() {
      this.pruneUnavailableSelections();
      this.elements.title.textContent = this.config.texts.title.replace('{vehicle}', this.vehicleLabel ?? '');
      this.elements.steps.replaceChildren(this.renderMaterialStep(), ...this.visibleSteps.map((step, index) => this.renderProductStep(step, index + 2)));
      this.renderSummary();
      window.updateCompareUI?.();
      this.scrollToNewPanel();
    }

    // Scroll to a related/required panel that appeared after the last interaction
    scrollToNewPanel() {
      const panels = [...this.elements.steps.querySelectorAll('[data-panel-key]')];
      const previousKeys = this.renderedPanelKeys;
      this.renderedPanelKeys = new Set(panels.map(({ dataset }) => dataset.panelKey));
      if (!previousKeys) return;

      const newPanel = panels.find(({ dataset }) => !previousKeys.has(dataset.panelKey));
      newPanel?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }

    renderStep(number, title, hint, tiles, { isMultiSelect = false, footer = null, afterTiles = null, beforeTiles = null } = {}) {
      return createElement('section', { className: 'set-builder__step' }, [
        createElement('header', { className: 'set-builder__step-header' }, [
          createElement('span', { className: 'set-builder__step-number', text: String(number) }),
          createElement('h3', { className: 'set-builder__step-title', text: title }),
          hint && createElement('span', { className: 'set-builder__step-hint', text: hint }),
        ]),
        beforeTiles,
        createElement('div', { className: 'set-builder__tiles', attributes: { role: isMultiSelect ? 'group' : 'radiogroup', 'aria-label': title } }, tiles),
        afterTiles,
        footer,
      ]);
    }

    renderTile({ title, subtitle, imageUrl, imageAlt, variant = '', isSelected, onSelect, product = null, isMultiSelect = false, badge = '', relatedToggle = null, variantGroup = null }) {
      const selectButton = createElement(
        'button',
        {
          className: 'set-builder__tile-select',
          attributes: { type: 'button', role: isMultiSelect ? 'checkbox' : 'radio', 'aria-checked': String(isSelected) },
        },
        [
          createElement('span', { className: 'set-builder__tile-media' }, [
            imageUrl ? createElement('img', { attributes: { src: imageUrl, alt: imageAlt ?? '', loading: 'lazy' } }) : createElement('span', { text: '—' }),
          ]),
          createElement('span', { className: 'set-builder__tile-title', text: title }),
          createElement('span', { className: 'set-builder__tile-subtitle', text: subtitle }),
        ],
      );
      selectButton.addEventListener('click', onSelect);

      const className = ['set-builder__tile', variant && `set-builder__tile--${variant}`, isSelected && 'is-selected'].filter(Boolean).join(' ');
      const badges = (badge || relatedToggle || variantGroup) && createElement('div', { className: 'set-builder__tile-badges' }, [
        variantGroup && this.renderVariantBadge(variantGroup),
        relatedToggle,
        badge && createElement('span', { className: 'set-builder__tile-badge', text: badge }),
      ]);
      return createElement('div', { className }, [selectButton, badges, product && this.renderProductActions(product)]);
    }

    renderProductActions(product) {
      const { texts } = this.config;
      return createElement('div', { className: 'set-builder__tile-actions' }, [
        createElement('a', {
          className: 'set-builder__tile-link',
          text: texts.viewProduct,
          attributes: { href: buildProductUrl(product.handle) },
        }),
        createElement('button', {
          className: 'card__compare-toggle set-builder__tile-compare',
          text: texts.compare,
          attributes: { type: 'button', 'data-compare-toggle': product.handle, 'aria-label': texts.compareAriaLabel },
        }),
      ]);
    }

    renderMaterialStep() {
      const { texts } = this.config;
      const tiles = this.displayedMaterialGroups.map((group) => {
        const groupProducts = this.getProductsForMaterial(group);
        const lowestPrice = Math.min(...groupProducts.map(({ price }) => price).filter(Number.isFinite), Number.POSITIVE_INFINITY);
        const sampleProduct = groupProducts[0];

        return this.renderTile({
          title: group.label,
          subtitle: group.sublabel || `${texts.from} ${this.formatMoney(lowestPrice, sampleProduct?.currency)}`,
          imageUrl: group.imageUrl || sampleProduct?.imageUrl,
          isSelected: group.id === this.displayedActiveMaterialGroup.id,
          onSelect: () => this.selectMaterialGroup(group.id),
        });
      });

      return this.renderStep(1, texts.materialStepTitle, texts.materialStepHint, tiles);
    }

    renderProductStep(step, number) {
      const isBundleStep = this.isBundleStep(step);
      const isMultiSelect = this.isMultiSelectStep(step);
      const selectedIds = this.getSelectedIds(step);
      const variantGroups = this.getStepVariantGroups(step);
      const stepProducts = this.filterByActiveVariant(step, this.getStepProducts(step), variantGroups);
      const visibleProducts = this.getVisibleStepProducts(step, stepProducts, isBundleStep);
      const productTiles = visibleProducts.map((product) =>
        this.renderTile({
          title: product.shortTitle,
          subtitle: this.formatMoney(product.price, product.currency),
          imageUrl: product.imageUrl,
          imageAlt: product.imageAlt,
          isSelected: isBundleStep ? this.isBundleSelected(product.id) : selectedIds.includes(product.id),
          onSelect: () => (isBundleStep ? this.selectBundle(product) : this.toggleStepProduct(step, product.id)),
          product,
          isMultiSelect,
          badge: this.getTileBadge(product),
          relatedToggle: isBundleStep ? null : this.renderRelatedBadge(product, selectedIds.includes(product.id)),
          variantGroup: variantGroups.length ? VARIANT_GROUPS.find(({ id }) => id === product.variantGroupId) : null,
        }),
      );

      const skipTile =
        step.allowSkip &&
        !isBundleStep &&
        this.renderTile({
          title: this.config.texts.skip,
          subtitle: '—',
          variant: 'skip',
          isSelected: !selectedIds.length,
          onSelect: () => this.clearStepSelection(step),
        });

      const hint = isMultiSelect ? [step.hint, this.config.texts.multiSelectHint].filter(Boolean).join(' · ') : step.hint;
      const selectedOwners = isBundleStep ? [] : selectedIds.map((id) => this.findProduct(id)).filter(Boolean);
      const relatedPanels = selectedOwners
        .flatMap((owner) => [this.renderRequiredPanel(owner), this.renderRelatedPanel(owner)])
        .filter(Boolean);
      const footer = relatedPanels.length ? createElement('div', { className: 'set-builder__related-group' }, relatedPanels) : null;
      const afterTiles = this.renderShowMoreButton(step, stepProducts.length - visibleProducts.length);
      const beforeTiles = this.renderVariantFilter(step, variantGroups);
      return this.renderStep(number, step.title, hint, [...productTiles, skipTile], { isMultiSelect, footer, afterTiles, beforeTiles });
    }
          // Variant filter (e.g. top / bottom spoiler cap): only when a step really has both kinds
          getStepVariantGroups(step) {
            const groupIds = new Set(this.getStepProducts(step).map(({ variantGroupId }) => variantGroupId).filter(Boolean));
            return groupIds.size > 1 ? VARIANT_GROUPS.filter(({ id }) => groupIds.has(id)) : [];
          }
    
          getActiveVariantFilter(step) {
            return this.state.variantFilters.get(step.id) ?? VARIANT_FILTER_ALL;
          }
    
          filterByActiveVariant(step, products, variantGroups) {
            const activeFilter = this.getActiveVariantFilter(step);
            if (!variantGroups.length || activeFilter === VARIANT_FILTER_ALL) return products;
            return products.filter(({ variantGroupId }) => variantGroupId === activeFilter);
          }
    
          setVariantFilter(step, filterId) {
            this.state.variantFilters.set(step.id, filterId);
            this.render();
          }
    
          renderVariantFilter(step, variantGroups) {
            if (!variantGroups.length) return null;
            const { texts } = this.config;
            const stepProducts = this.getStepProducts(step);
            const activeFilter = this.getActiveVariantFilter(step);
            const filters = [
              { id: VARIANT_FILTER_ALL, label: texts.variantAll, count: stepProducts.length },
              ...variantGroups.map(({ id, textKey }) => ({
                id,
                label: texts[textKey],
                count: stepProducts.filter(({ variantGroupId }) => variantGroupId === id).length,
              })),
            ];
    
            return createElement(
              'div',
              { className: 'set-builder__variant-filter', attributes: { role: 'group', 'aria-label': step.title } },
              filters.map(({ id, label, count }) => {
                const isActive = id === activeFilter;
                const button = createElement(
                  'button',
                  { className: `set-builder__variant-option${isActive ? ' is-active' : ''}`, attributes: { type: 'button', 'aria-pressed': String(isActive) } },
                  [createElement('span', { text: label }), createElement('span', { className: 'set-builder__variant-count', text: String(count) })],
                );
                button.addEventListener('click', () => this.setVariantFilter(step, id));
                return button;
              }),
            );
          }
    
          renderVariantBadge(variantGroup) {
            const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            icon.setAttribute('viewBox', '0 0 24 24');
            icon.setAttribute('aria-hidden', 'true');
            icon.innerHTML = `<rect x="3" y="5" width="18" height="14" rx="3" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="${variantGroup.icon}" fill="currentColor"/>`;
            return createElement('span', { className: 'set-builder__tile-badge set-builder__tile-badge--variant' }, [
              icon,
              createElement('span', { text: this.config.texts[variantGroup.textKey] }),
            ]);
          }

    // Collapsed steps (show more)
    getVisibleStepProducts(step, stepProducts, isBundleStep) {
      if (this.state.expandedStepIds.has(step.id) || stepProducts.length <= COLLAPSED_TILE_LIMIT) return stepProducts;
      const pinnedIds = new Set(
        isBundleStep ? stepProducts.filter(({ id }) => this.isBundleSelected(id)).map(({ id }) => id) : this.getSelectedIds(step),
      );
      const fillCount = Math.max(COLLAPSED_TILE_LIMIT - stepProducts.filter(({ id }) => pinnedIds.has(id)).length, 0);
      const fillIds = new Set(stepProducts.filter(({ id }) => !pinnedIds.has(id)).slice(0, fillCount).map(({ id }) => id));
      return stepProducts.filter(({ id }) => pinnedIds.has(id) || fillIds.has(id));
    }

    toggleStepExpanded(step) {
      const { expandedStepIds } = this.state;
      expandedStepIds.has(step.id) ? expandedStepIds.delete(step.id) : expandedStepIds.add(step.id);
      this.render();
    }

    renderShowMoreButton(step, hiddenCount) {
      const isExpanded = this.state.expandedStepIds.has(step.id);
      if (!isExpanded && hiddenCount <= 0) return null;
      const { showMore = 'Pokaż pozostałe ({count})', showLess = 'Pokaż mniej' } = this.config.texts;
      const button = createElement('button', {
        className: 'set-builder__show-more',
        text: isExpanded ? showLess : showMore.replace('{count}', String(hiddenCount)),
        attributes: { type: 'button', 'aria-expanded': String(isExpanded) },
      });
      button.addEventListener('click', () => this.toggleStepExpanded(step));
      return button;
    }

    renderRelatedBadge(owner, isSelected) {
      const relatedCount = this.getRelatedProducts(owner).length;
      if (!relatedCount) return null;
    
      return createElement('span', {
        className: `set-builder__related-toggle${isSelected ? ' is-active' : ''}`,
        text: this.formatRelatedBadge(this.config.texts.relatedBadge, relatedCount),
      });
    }
    
    // --- Related badge label ---
    formatRelatedBadge(label = '', count) {
      return label.includes('{count}')
        ? label.replace('{count}', String(count))
        : `+${count} ${label}`.trim();
    }

    renderRelatedPanel(owner) {
      const { texts } = this.config;
      return this.renderProductListPanel(owner, this.getRelatedProducts(owner), { title: texts.relatedTitle, hint: texts.relatedHint });
    }

    renderRequiredPanel(owner) {
      const { texts } = this.config;
      return this.renderProductListPanel(owner, this.getRequiredProducts(owner), { title: texts.requiredTitle, hint: texts.requiredHint, variant: 'required' });
    }

    renderProductListPanel(owner, items, { title, hint, variant = '' }) {
      if (!items.length) return null;
      const className = ['set-builder__related', variant && `set-builder__related--${variant}`].filter(Boolean).join(' ');
      const panelKey = `${variant || 'related'}:${owner.id}`;

      return createElement('div', { className, attributes: { 'data-panel-key': panelKey } }, [
        createElement('div', { className: 'set-builder__related-header' }, [
          owner.imageUrl && createElement('img', { className: 'set-builder__related-thumb', attributes: { src: owner.imageUrl, alt: '', loading: 'lazy' } }),
          createElement('div', { className: 'set-builder__related-heading' }, [
            createElement('span', { className: 'set-builder__related-eyebrow', text: title }),
            createElement('span', { className: 'set-builder__related-owner', text: owner.shortTitle }),
          ]),
          hint && createElement('span', { className: 'set-builder__related-hint', text: hint }),
        ]),
        createElement('ul', { className: 'set-builder__related-list' }, items.map((item) => this.renderRelatedItem(owner, item))),
      ]);
    }

    renderRelatedItem(owner, item) {
      const { texts } = this.config;
      const { product, step } = item;
      const checkbox = createElement('input', {
        className: 'set-builder__related-checkbox',
        attributes: { type: 'checkbox', checked: this.isProductSelected(product, step), 'aria-label': product.shortTitle },
      });
      checkbox.addEventListener('change', () => this.toggleRelatedProduct(owner, item));

      return createElement('li', { className: 'set-builder__related-item' }, [
        createElement('label', { className: 'set-builder__related-label' }, [
          checkbox,
          product.imageUrl && createElement('img', { className: 'set-builder__related-image', attributes: { src: product.imageUrl, alt: '', loading: 'lazy' } }),
          createElement('span', { className: 'set-builder__related-title', text: product.shortTitle }),
        ]),
        createElement('span', { className: 'set-builder__related-price', text: `+ ${this.formatMoney(product.price, product.currency)}` }),
        createElement('a', {
          className: 'set-builder__related-link',
          text: texts.relatedView,
          attributes: { href: buildProductUrl(product.handle), target: '_blank', rel: 'noopener' },
        }),
      ]);
    }

    // Standalone page: info about the product the customer came from
    renderStartBanner() {
      const { startBanner } = this.elements;
      const product = this.startProduct;
      if (!startBanner || !product) return;

      const { startBanner: template = '', close } = this.config.texts;
      const [textBefore, textAfter = ''] = template.split('{product}');
      const closeButton = createElement('button', {
        className: 'set-builder__start-close',
        text: '×',
        attributes: { type: 'button', 'aria-label': close },
      });
      closeButton.addEventListener('click', () => {
        startBanner.hidden = true;
      });

      startBanner.replaceChildren(
        ...[
          product.imageUrl && createElement('img', { className: 'set-builder__start-image', attributes: { src: product.imageUrl, alt: '', loading: 'lazy' } }),
          createElement('p', { className: 'set-builder__start-text' }, [
            document.createTextNode(textBefore),
            createElement('strong', { text: product.shortTitle }),
            document.createTextNode(textAfter),
          ]),
          closeButton,
        ].filter(Boolean),
      );
      startBanner.hidden = false;
    }

    renderSummary() {
      const { texts } = this.config;
      const products = this.selectedProducts;
      const total = products.reduce((sum, { price }) => sum + (Number.isFinite(price) ? price : 0), 0);
      const currency = products[0]?.currency;

      this.elements.summaryTitle.textContent = texts.summaryTitle
        .replace('{material}', this.displayedActiveMaterialGroup.label.split(' · ')[0])
        .replace('{count}', String(products.length));
      this.elements.summaryList.replaceChildren(
        // ...products.map((product) =>
        //   createElement('li', { className: 'set-builder__summary-item' }, [
        //     createElement('span', { text: product.shortTitle }),
        //     createElement('span', { text: this.formatMoney(product.price, product.currency) }),
        //   ]),
        // ),
        ...products.map((product) => {
          const removeButton = createElement('button', {
            className: 'set-builder__summary-remove',
            text: '×',
            attributes: { type: 'button', 'aria-label': `remove ${product.shortTitle}` },
          });
          removeButton.addEventListener('click', () => this.removeSelectedProduct(product.id));

          return createElement('li', { className: 'set-builder__summary-item' }, [
            removeButton,
            createElement('span', { className: 'set-builder__summary-name', text: product.shortTitle }),
            createElement('span', { text: this.formatMoney(product.price, product.currency) }),
          ]);
        }),
      );
      this.elements.total.textContent = this.formatMoney(total, currency);
      this.elements.addToCart.disabled = !products.length;
    }

    // Cart
    buildCartItems() {
      const bundleId = `bundle_${Date.now()}`;
      return this.selectedProducts.map((product, index) => ({
        id: product.variantId,
        quantity: 1,
        properties: { _bundle: 'true', _bundle_id: bundleId, _component_index: String(index + 1) },
      }));
    }

    async addSelectionToCart() {
      const { addToCart, status } = this.elements;
      const cart = document.querySelector('cart-drawer') ?? document.querySelector('cart-notification');
      const sections = cart?.getSectionsToRender?.().map((section) => section.section ?? section.id) ?? [];

      addToCart.disabled = true;
      status.textContent = '';

      try {
        const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: this.buildCartItems(), sections, sections_url: window.location.pathname }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description ?? result.message ?? `HTTP ${response.status}`);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'set-builder', cartData: result });
        }
        cart?.classList.remove('is-empty');
        cart?.renderContents?.(result);
      } catch (error) {
        console.error('[set-builder] add to cart failed', error);
        status.textContent = this.config.texts.addToCartError;
      } finally {
        addToCart.disabled = false;
      }
    }
  }

  customElements.define('maxton-set-builder', MaxtonSetBuilder);
})();